// src/components/Form/attendanceview.jsx
import React, { useState, useEffect, useContext, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import {
  FaArrowLeft, FaUser, FaCalendarAlt, FaClock, FaCheck, FaTimes,
  FaEdit, FaTrash, FaEye, FaSearch, FaFilter, FaBriefcase, FaBed,
  FaStar, FaExclamationTriangle, FaHourglassHalf, FaSignOutAlt, FaSignInAlt,
  FaBusinessTime, FaCheckDouble, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './CreateCustomerPage.css';

const AttendanceView = () => {
  const navigate = useNavigate();
  const { user, baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]); // Add logic to fetch shifts
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [summary, setSummary] = useState(null);
  const [leaveApps, setLeaveApps] = useState([]); // NEW: Store leaves

  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => parseInt(new Date().getFullYear()));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  // Global State

  // Modal States
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null); // Stores ID of record to delete

  // Permission State
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [dataScope, setDataScope] = useState("All Data");
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [userBranch, setUserBranch] = useState("");
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Initial Fetch: Permissions, Employees & Shifts
  useEffect(() => {
    // Wait for config to load
    if (configLoading) return;

    const init = async () => {
      setLoading(true);
      try {
        const currentBaseUrl = baseUrl || '';
        const scope = await fetchPermissions(currentBaseUrl);

        const empRes = await axios.get(`${baseUrl}/api/add-employee`, {
          headers: getHeaders(),
          params: {
            userId: user?.userId || user?._id || user?.id,
            role: user?.role,
            pageId: 'view_attendance',
            branch_name: selectedBranchFilter
          }
        });
        let employeeList = empRes.data || [];

        if (employeeList.length === 1 && !selectedEmployee) {
          const onlyEmp = employeeList[0];
          setSelectedEmployee(onlyEmp._id || onlyEmp.id);
        }

        setEmployees(employeeList);

        const shiftRes = await axios.get(`${currentBaseUrl}/api/schedules`, { headers: getHeaders() });
        setShifts(shiftRes.data || []);

      } catch (err) {
        console.error("Init Error:", err);
        setError("Failed to initialize. Ensure server is running.");
      }
    };

    const fetchPermissions = async (currentBaseUrl) => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const u = JSON.parse(userStr);
          const role = u.role;
          setIsCompanyAdmin(role === 'company_admin' || role === 'admin');
          setIsGroupAdmin(role === 'group_admin' || role === 'Super Admin' || u.isGlobalAdmin === true);
          setUserBranch(u.branch_name || u.branch || "");
          if (role) {
            const userEmail = u.email || '';
            const url = `${currentBaseUrl}/api/role-permissions?role=${role}&email=${userEmail}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'view_attendance');
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
              const scope = pagePerm.dataAccess || pagePerm.dataScope || "All Data";
              setDataScope(scope);
              return scope;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
      return "All Data";
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/branches`, { headers: getHeaders() });
        const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        setAvailableBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    init();
    fetchBranches();
  }, [baseUrl, configLoading, user, selectedBranchFilter]);

  // Fetch Attendance when filters change
  useEffect(() => {
    // Only fetch if month is selected. 
    // baseUrl can be an empty string in server mode, so we don't check for truthiness,
    // but we ensure it's not undefined (meaning useState has initialized)
    if (selectedMonth && baseUrl !== undefined) {
      fetchData();
    } else {
      setAttendances([]);
      setSummary(null);
    }
  }, [baseUrl, selectedEmployee, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${baseUrl}/api/attendance`;
      const response = await axios.get(url, {
        headers: getHeaders(),
        params: {
          employee_id: selectedEmployee,
          month: selectedMonth,
          userId: user?.userId || user?._id || user?._id,
          role: user?.role
        }
      });

      // NEW: Fetch Approved Leaves
      try {
        const leaveRes = await axios.get(`${baseUrl}/api/leave-applications`, {
          headers: getHeaders(),
          params: {
            employee_id: selectedEmployee,
            // Backend supports filtering by employee_id when access is ALL, or logic handles OWN
            userId: user?.userId || user?._id || user?.id,
            role: user?.role
          }
        });
        // Filter strictly for this view if needed, but backend should handle scope
        setLeaveApps(leaveRes.data || []);
      } catch (err) {
        console.warn("Failed to fetch leaves", err);
      }

      let data = [];
      if (response.data.summary && response.data.records) {
        data = response.data.records;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data.records) {
        data = response.data.records;
      }

      setAttendances(data);
      calculateSummary(data);

      setLoading(false);
    } catch (err) {
      console.error('Fetch Error:', err);
      setError(`Failed to fetch data: ${err.response?.data?.error || err.message}`);
      setLoading(false);
    }
  };

  // Helper to get plan time
  const getPlannedTimeDisplay = (att) => {
    return `${att.planned_start_time || '--:--'} - ${att.planned_end_time || '--:--'}`;
  };

  // Helper to get days in month
  const getDaysInMonth = (monthStr) => {
    if (!monthStr) return [];
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1, 1);
    const days = [];
    while (date.getMonth() === month - 1) {
      days.push(new Date(date).toISOString().split('T')[0]);
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  // Group records by Employee + Date
  const getGroupedAttendances = () => {
    // If no employee selected, just show raw list (admin view typically)
    // But if "Own Data" user, selectedEmployee is auto-set.
    if (!selectedEmployee) {
      const groups = {};
      attendances.forEach(att => {
        const key = `${att.attendance_date}_${att.employee_id}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(att);
      });
      return Object.values(groups).map(group => processGroup(group)).sort((a, b) => b.attendance_date.localeCompare(a.attendance_date));
    }

    // If Employee Selected: Generate Full Calendar
    let days = getDaysInMonth(selectedMonth);
    const employeeDetails = employees.find(e => e._id === selectedEmployee || e.id === selectedEmployee);

    // NEW: Filter out future dates (Stop at today)
    const today = new Date().toISOString().split('T')[0];
    days = days.filter(d => d <= today);

    return days.map(date => {
      // Find existing records for this date
      const dayRecords = attendances.filter(a => a.attendance_date === date && (a.employee_id === selectedEmployee || a.employee?._id === selectedEmployee));

      if (dayRecords.length > 0) {
        return processGroup(dayRecords);
      }

      // Check for Approved Leave
      const approvedLeave = leaveApps.find(l => {
        // Handle date string formats
        const start = l.from_date.split('T')[0];
        const end = l.to_date.split('T')[0];
        // Match employee and date range and status
        const isEmpMatch = !selectedEmployee || (l.employee_id === selectedEmployee || l.employee_id?._id === selectedEmployee || l.employee?._id === selectedEmployee);
        return isEmpMatch && l.status === 'APPROVED' && date >= start && date <= end;
      });

      if (approvedLeave) {
        return {
          attendance_date: date,
          employee_id: selectedEmployee,
          employee: employeeDetails || { name: 'Unknown', employeeId: 'N/A' },
          status: approvedLeave.leave_name || 'Paid Leave',
          planned_time_display: 'On Leave',
          actual_time_display: '---',
          worked_minutes: 0,
          overtime_minutes: 0,
          late_minutes: 0,
          early_exit_minutes: 0,
          notes: `Leave: ${approvedLeave.leave_name} (${approvedLeave.leave_mode})`,
          is_virtual: true,
          color: '#e67e22' // Orange/Carrot for leave
        };
      }

      // No record? Generate placeholder
      const dayOfWeek = new Date(date).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sun (0), Sat (6)

      return {
        attendance_date: date,
        employee_id: selectedEmployee,
        employee: employeeDetails || { name: 'Unknown', employeeId: 'N/A' },
        status: isWeekend ? 'WeeklyOff' : 'N/A', // Default to WeeklyOff for Sat/Sun
        planned_time_display: '--:-- - --:--',
        actual_time_display: '---',
        worked_minutes: 0,
        overtime_minutes: 0,
        late_minutes: 0,
        early_exit_minutes: 0,
        notes: isWeekend ? 'Weekly Off' : 'No Record',
        is_virtual: true // Mark as virtual so we don't try to edit/delete
      };
    }).sort((a, b) => b.attendance_date.localeCompare(a.attendance_date));
  };

  const processGroup = (group) => {
    const base = { ...group[0] }; // Clone first record as base

    // Ensure numeric fields are numbers
    base.worked_minutes = Number(base.worked_minutes) || 0;
    base.overtime_minutes = Number(base.overtime_minutes) || 0;
    base.late_minutes = Number(base.late_minutes) || 0;
    base.early_exit_minutes = Number(base.early_exit_minutes) || 0;

    if (group.length > 1) {
      // It's a split shift day with multiple EXISTING records
      // Sort by time
      group.sort((a, b) => (a.planned_start_time || '').localeCompare(b.planned_start_time || ''));

      // Merge Fields for Display
      base.planned_time_display = group.map(g => `${g.planned_start_time}-${g.planned_end_time}`).join(', ');
      base.actual_time_display = group.map(g => `${g.actual_check_in || 'N/A'}-${g.actual_check_out || 'N/A'}`).join(', ');

      // Summing up numeric stats for the day
      base.worked_minutes = group.reduce((sum, g) => sum + (Number(g.worked_minutes) || 0), 0);
      base.overtime_minutes = group.reduce((sum, g) => sum + (Number(g.overtime_minutes) || 0), 0);
      base.late_minutes = group.reduce((sum, g) => sum + (Number(g.late_minutes) || 0), 0);
      base.early_exit_minutes = group.reduce((sum, g) => sum + (Number(g.early_exit_minutes) || 0), 0);

      // Combine unique notes
      const allNotes = [...new Set(group.map(g => g.notes).filter(n => n))].join('; ');
      base.notes = allNotes;

      // Mark as merged
      base.isMerged = true;
      base.relatedRecords = group;
    } else {
      // Single Record
      base.planned_time_display = getPlannedTimeDisplay(base);
      // Only show actual times if Status is Present/Late/HalfDay etc, or if data exists
      if (base.actual_check_in || base.actual_check_out) {
        base.actual_time_display = `${base.actual_check_in || 'N/A'} - ${base.actual_check_out || 'N/A'}`;
      } else {
        base.actual_time_display = '---';
      }
    }
    return base;
  };

  const groupedAttendances = getGroupedAttendances();

  const calculateSummary = (data) => {
    const stats = {
      total_days: 0,
      working_days: 0,
      present_days: 0,
      absent_days: 0,
      weekly_offs: 0,
      holidays: 0,
      leave_days: 0,
      half_days: 0,
      total_overtime_mins: 0,
      total_late_mins: 0,
      total_early_mins: 0
    };

    // If we have groupedAttendances (which includes virtual days), use that for summary!
    // But calculateSummary is called in fetchData with raw data. 
    // We should probably rely on groupedAttendances for the summary to be accurate with the view.
    // However, recalculating summary from render data is safer.

    // Let's use the generated groupedAttendances for summary if available
    const sourceData = groupedAttendances.length > 0 ? groupedAttendances : data;

    const uniqueDays = new Set();

    sourceData.forEach(att => {
      const key = `${att.attendance_date}_${att.employee_id}`;
      // Logic: For numeric aggregates (OT, Late), we sum ALL records (even split shifts). 
      // For Day Counts, we simpler logic: we count the day once based on "primary" status 
      // OR we just iterate all records but handle the "Day" count uniquely.

      // Let's iterate all records for Minutes summation
      stats.total_overtime_mins += (Number(att.overtime_minutes) || 0);
      stats.total_late_mins += (Number(att.late_minutes) || 0);
      stats.total_early_mins += (Number(att.early_exit_minutes) || 0);

      if (!uniqueDays.has(key)) {
        uniqueDays.add(key);
        stats.total_days++;

        const status = att.status;
        const specialType = att.special_day_type;

        // Categorize Status (Prioritize Holidays and Weekly Offs)
        if (status === 'Holiday' || specialType === 'Holiday') stats.holidays++;
        else if (status === 'WeeklyOff' || specialType === 'WeeklyOff') stats.weekly_offs++;
        else if (status === 'Present') stats.present_days++;
        else if (status === 'Absent') stats.absent_days++;
        else if (['On Leave', 'Leave', 'Paid Leave'].includes(status)) stats.leave_days++;
        else if (status === 'HalfDay' || specialType === 'HalfDay') stats.half_days++;

        // Generic Working Days (Present, HalfDay, Extended)
        const nonWorkingStatuses = ['Absent', 'On Leave', 'Leave', 'Paid Leave', 'WeeklyOff', 'Holiday', 'N/A'];
        if (!nonWorkingStatuses.includes(status)) {
          stats.working_days++;
        }
      }
    });

    setSummary(stats);
  };

  // Convert minutes to HH:MM
  const minsToTime = (mins) => {
    if (!mins) return "0h 0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  // Delete Handlers
  const initiateDelete = (id) => setItemToDelete(id);
  const confirmDelete = async () => {
    if (!canDelete) {
      setError("You do not have permission to delete attendance records.");
      setItemToDelete(null);
      return;
    }
    if (!itemToDelete) return;
    try {
      await axios.delete(`${baseUrl}/api/attendance`, {
        data: { _id: itemToDelete },
        headers: getHeaders()
      });
      setMessage('Record deleted successfully');
      fetchData();
    } catch (err) {
      setError(`Delete failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setItemToDelete(null);
    }
  };
  const cancelDelete = () => setItemToDelete(null);

  const handleEdit = (rec) => {
    if (!canWrite) {
      setError("You do not have permission to edit attendance records.");
      return;
    }
    // Navigate by Employee & Date
    navigate(`/attendance?employee_id=${rec.employee_id}&date=${rec.attendance_date}`, {
      state: { attendance: rec.isMerged ? rec.relatedRecords[0] : rec }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return '#27ae60';
      case 'Absent': return '#e74c3c';
      case 'WeeklyOff': return '#95a5a6';
      case 'Holiday': return '#8e44ad';
      case 'Extended': return '#f39c12';
      case 'HalfDay': return '#d35400';
      case 'On Leave':
      case 'Leave': return '#e67e22';
      case 'Paid Leave': return '#16a085';
      default: return '#2c3e50';
    }
  };

  // Trigger summary calculation when groupedAttendances changes
  useEffect(() => {
    if (groupedAttendances.length > 0) {
      calculateSummary(groupedAttendances);
    }
  }, [JSON.stringify(groupedAttendances)]); // Deep compare to avoid loops

  class LocalErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    render() {
      if (this.state.hasError) return <div style={{padding: '50px', color: 'red'}}><h1>Render Error in AttendanceView:</h1><pre>{this.state.error.toString()}</pre></div>;
      return this.props.children;
    }
  }

  return (
    <LocalErrorBoundary>
    <div className="create-customer-container main-customer-page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: '100vh', backgroundColor: '#FAFBFD', fontFamily: "'Inter', sans-serif", width: '100%', padding: '24px 0 0 0', boxSizing: 'border-box' }}>
      <style>
        {`
          .premium-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
            color: #1B1B29;
          }
          .premium-table th {
            text-align: left;
            padding: 18px 24px;
            font-weight: 700;
            color: #4A4A5A;
            text-transform: uppercase;
            font-size: 12px;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #E2E6EF;
            background: #ffffff;
            white-space: nowrap;
          }
          .premium-table td {
            padding: 16px 24px;
            font-weight: 500;
            border-bottom: 1px solid #F1F3F8;
            transition: all 0.2s ease;
            vertical-align: middle;
          }
          .premium-table tr:nth-child(odd) td { background: #ffffff; }
          .premium-table tr:nth-child(even) td { background: #FAFBFD; }
          .premium-table tr:hover td { background: #F1F4F9; }
          .premium-table tr:last-child td { border-bottom: none; }
          
          .action-btn {
            width: 42px;
            height: 42px;
            border-radius: 10px;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
          }
          .action-btn-view { background: rgba(75, 118, 230, 0.15); color: #4B76E6; }
          .action-btn-view:hover { background: #4B76E6; color: #ffffff; box-shadow: 0 4px 12px rgba(75, 118, 230, 0.3); }
          .action-btn-edit { background: rgba(255, 159, 4, 0.15); color: #FF9F04; }
          .action-btn-edit:hover { background: #FF9F04; color: #ffffff; box-shadow: 0 4px 12px rgba(255, 159, 4, 0.3); }
          .action-btn-delete { background: rgba(255, 89, 94, 0.15); color: #FF595E; }
          .action-btn-delete:hover { background: #FF595E; color: #ffffff; box-shadow: 0 4px 12px rgba(255, 89, 94, 0.3); }

          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: #DCE3EE; border-radius: 10px; }
          ::-webkit-scrollbar-thumb:hover { background: #A0A5B5; }

          @keyframes fadeInScale {
            from { opacity: 0; transform: scale(0.95) translateY(10px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          .premium-card {
            animation: fadeInScale 0.4s cubic-bezier(0, 0, 0.2, 1);
          }
        `}
      </style>

      {/* Top Bar Section */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate('/admin')} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>Detailed Attendance View</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            <button
              onClick={() => {
                if (!canWrite) {
                  setError("You do not have permission to create attendance records.");
                  return;
                }
                navigate('/attendance');
              }}
              className="save-btn"
              style={{ backgroundColor: '#20c997', width: 'auto', padding: '0 20px', display: 'flex', alignItems: 'center', gap: '8px', height: '44px', borderRadius: '25px', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
            >
              <FaEdit /> Add / Manual Entry
            </button>
          </div>
        </div>
      </div>

      {/* Filter Bar & Search Row */}
      <div style={{ padding: "16px 48px 24px 48px", flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
          
          <div style={{ flex: 1, minWidth: '200px', maxWidth: '250px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#777777', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select Month</label>
            <div style={{ position: 'relative' }}>
              <FaCalendarAlt style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#4B76E6', fontSize: '16px', zIndex: 2 }} />
              <div 
                onClick={() => { setShowMonthPicker(!showMonthPicker); setPickerYear(parseInt(selectedMonth.split('-')[0])); }}
                style={{ width: '100%', height: '48px', padding: '0 16px 0 44px', display: 'flex', alignItems: 'center', border: '2px solid #F3F6FB', borderRadius: '12px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', backgroundColor: '#F3F6FB', cursor: 'pointer', transition: 'all 0.2s', boxSizing: 'border-box' }}
              >
                {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
              </div>

              {showMonthPicker && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: '280px', background: '#ffffff', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', padding: '20px', zIndex: 100 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <button onClick={() => setPickerYear(y => y - 1)} style={{ background: '#F8F9FA', border: 'none', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background='#F3F6FB'} onMouseOut={e => e.currentTarget.style.background='#F8F9FA'}><FaChevronLeft fontSize="14px" /></button>
                    <span style={{ fontWeight: '800', fontSize: '16px', color: '#1B1B29' }}>{pickerYear}</span>
                    <button onClick={() => setPickerYear(y => y + 1)} style={{ background: '#F8F9FA', border: 'none', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background='#F3F6FB'} onMouseOut={e => e.currentTarget.style.background='#F8F9FA'}><FaChevronRight fontSize="14px" /></button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, index) => {
                      const monthValue = `${pickerYear}-${String(index + 1).padStart(2, '0')}`;
                      const isSelected = selectedMonth === monthValue;
                      return (
                        <button
                          key={m}
                          onClick={() => { setSelectedMonth(monthValue); setShowMonthPicker(false); }}
                          style={{
                            padding: '12px 0',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: '700',
                            fontSize: '14px',
                            cursor: 'pointer',
                            background: isSelected ? '#604BE8' : '#F8F9FA',
                            color: isSelected ? '#ffffff' : '#1B1B29',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={e => { if(!isSelected) e.currentTarget.style.background = '#F3F6FB'; }}
                          onMouseOut={e => { if(!isSelected) e.currentTarget.style.background = '#F8F9FA'; }}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {(isGroupAdmin || (isCompanyAdmin && !userBranch)) && (
            <div style={{ flex: 1, minWidth: '200px', maxWidth: '250px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#777777', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Branch</label>
              <div style={{ position: 'relative' }}>
                <FaBriefcase style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#4B76E6', fontSize: '16px' }} />
                <select
                  value={selectedBranchFilter}
                  onChange={(e) => setSelectedBranchFilter(e.target.value)}
                  style={{ width: '100%', height: '48px', padding: '0 16px 0 44px', border: '2px solid #F3F6FB', borderRadius: '12px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', backgroundColor: '#F3F6FB', appearance: 'none', cursor: 'pointer', transition: 'all 0.2s', boxSizing: 'border-box' }}
                  onFocus={e => { e.target.style.borderColor = '#4B76E6'; e.target.style.backgroundColor = '#ffffff'; }}
                  onBlur={e => { e.target.style.borderColor = '#F3F6FB'; e.target.style.backgroundColor = '#F3F6FB'; }}
                >
                  <option value="">All Branches</option>
                  {availableBranches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#777777', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select Employee</label>
            <div style={{ position: 'relative' }}>
              <FaUser style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#4B76E6', fontSize: '16px' }} />
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                style={{ width: '100%', height: '48px', padding: '0 16px 0 44px', border: '2px solid #F3F6FB', borderRadius: '12px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', backgroundColor: '#F3F6FB', appearance: 'none', cursor: 'pointer', transition: 'all 0.2s', boxSizing: 'border-box' }}
                onFocus={e => { e.target.style.borderColor = '#4B76E6'; e.target.style.backgroundColor = '#ffffff'; }}
                onBlur={e => { e.target.style.borderColor = '#F3F6FB'; e.target.style.backgroundColor = '#F3F6FB'; }}
              >
                <option value="">-- All Employees --</option>
                {employees.map(emp => (
                  <option key={emp._id || emp.id} value={emp._id || emp.id}>
                    {emp.name || emp.employeeName || 'No Name'}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>
      </div>

      {/* Messages - CENTRED OVERLAY */}
      {error && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', color: '#e74c3c', padding: '30px', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', zIndex: 2000, textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold', minWidth: '350px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <FaExclamationTriangle size={40} />
          <div>{error}</div>
          <button onClick={() => setError('')} style={{ marginTop: '10px', padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', alignSelf: 'center' }}>Dismiss</button>
        </div>
      )}

      {message && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', color: '#27ae60', padding: '30px', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', zIndex: 2000, textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold', minWidth: '350px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <FaCheckDouble size={40} />
          <div>{message}</div>
          <button onClick={() => setMessage('')} style={{ marginTop: '10px', padding: '10px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', alignSelf: 'center' }}>OK</button>
        </div>
      )}

      {/* Scrollable Data Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: "0 48px 48px" }}>
        
        {/* Detailed Summary Dashboard */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <SummaryWidget title="Total Days" value={summary.total_days} color="#4B76E6" icon={<FaCalendarAlt />} />
            <SummaryWidget title="Present" value={summary.present_days} color="#2ECC71" icon={<FaCheck />} />
            <SummaryWidget title="Absent" value={summary.absent_days} color="#FF595E" icon={<FaTimes />} />
            <SummaryWidget title="Week Offs" value={summary.weekly_offs} color="#94A3B8" icon={<FaBed />} />
            <SummaryWidget title="Holidays" value={summary.holidays} color="#9B59B6" icon={<FaStar />} />
            <SummaryWidget title="Leaves" value={summary.leave_days} color="#F39C12" icon={<FaSignOutAlt />} />
            <SummaryWidget title="Late Mins" value={summary.total_late_mins} color="#E67E22" icon={<FaHourglassHalf />} />
            <SummaryWidget title="Early Exit" value={summary.total_early_mins} color="#E74C3C" icon={<FaSignInAlt />} />
            <SummaryWidget title="Overtime" value={minsToTime(summary.total_overtime_mins)} color="#F1C40F" icon={<FaBusinessTime />} />
          </div>
        )}

        {/* Detailed Table */}
        <div style={{ borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', backgroundColor: '#ffffff', border: '1px solid #E2E6EF', overflow: 'hidden' }} className="premium-card">
          <div style={{ overflowX: 'auto' }}>
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Plan Time</th>
                  <th>Actual Time</th>
                  <th>Worked</th>
                  <th>Overtime</th>
                  <th>Late</th>
                  <th>Early Exit</th>
                  <th>Notes</th>
                  <th style={{ textAlign: 'right', paddingRight: '36px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="11" style={{ padding: '60px', textAlign: 'center', color: '#777777', fontSize: '15px' }}>⏳ Fetching attendance records...</td></tr>
                ) : groupedAttendances.length > 0 ? (
                  groupedAttendances.map((att, idx) => (
                    <tr key={idx}>
                      <td>
                        <div style={{ fontWeight: '700', color: '#1B1B29', fontSize: '14px' }}>{att.attendance_date}</div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: '500' }}>{new Date(att.attendance_date).toLocaleDateString('en-US', { weekday: 'long' })}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600', color: '#1B1B29' }}>{att.employee?.name || 'N/A'}</div>
                        <div style={{ fontSize: '12px', color: '#777777' }}>{att.employee?.employeeId || 'N/A'}</div>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                          backgroundColor: getStatusColor(att.status) + '15', color: getStatusColor(att.status)
                        }}>
                          {att.status}
                        </span>
                        {att.isMerged && <div style={{ fontSize: '11px', color: '#3498db', marginTop: '4px', fontWeight: '600' }}>(Split Shift)</div>}
                        {att.is_roster && <div style={{ fontSize: '11px', backgroundColor: '#8e44ad', color: 'white', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>Roster Duty</div>}
                      </td>
                      <td>{att.planned_time_display}</td>
                      <td>{att.actual_time_display}</td>
                      <td style={{ fontWeight: '700' }}>{minsToTime(att.worked_minutes)}</td>
                      <td style={{ color: att.overtime_minutes > 0 ? '#2ECC71' : 'inherit', fontWeight: att.overtime_minutes > 0 ? '700' : '500' }}>
                        {att.overtime_minutes > 0 ? minsToTime(att.overtime_minutes) : '-'}
                      </td>
                      <td style={{ color: att.late_minutes > 0 ? '#FF595E' : 'inherit', fontWeight: att.late_minutes > 0 ? '700' : '500' }}>
                        {att.late_minutes > 0 ? `${att.late_minutes} min` : '-'}
                      </td>
                      <td style={{ color: att.early_exit_minutes > 0 ? '#E67E22' : 'inherit', fontWeight: att.early_exit_minutes > 0 ? '700' : '500' }}>
                        {att.early_exit_minutes > 0 ? `${att.early_exit_minutes} min` : '-'}
                      </td>
                      <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={att.notes}>{att.notes || '-'}</td>
                      <td style={{ paddingRight: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                          <button onClick={() => setSelectedAttendance(att)} className="action-btn action-btn-view" title="View Details">
                            <FaEye style={{ fontSize: '18px' }} />
                          </button>
                          {!att.is_virtual && (
                            <button onClick={() => handleEdit(att)} className="action-btn action-btn-edit" title="Edit">
                              <FaEdit style={{ fontSize: '18px' }} />
                            </button>
                          )}
                          {!att.is_virtual && (
                            <button onClick={() => initiateDelete(att._id)} className="action-btn action-btn-delete" title="Delete">
                              <FaTrash style={{ fontSize: '18px' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" style={{ padding: '60px', textAlign: 'center' }}>
                      <div style={{ fontSize: '42px', marginBottom: '16px' }}>📅</div>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1B1B29', margin: '0 0 8px' }}>No Records Found</h3>
                      <p style={{ color: '#777777', margin: 0 }}>No attendance records found for the selected month.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- DETAIL MODAL --- */}
      {selectedAttendance && (
        <div style={itemsModalOverlay} onClick={() => setSelectedAttendance(null)}>
          <div style={itemsModalContent} onClick={e => e.stopPropagation()}>
            <div style={itemsModalHeader}>
              <h3>Full Details</h3>
              <button onClick={() => setSelectedAttendance(null)} style={closeBtn}><FaTimes /></button>
            </div>
            <div style={itemsModalBody}>
              <div style={gridContainer}>
                <DetailBox label="Date" value={selectedAttendance.attendance_date} />
                <DetailBox label="Employee" value={selectedAttendance.employee?.name || '--'} />
                <DetailBox label="Status" value={selectedAttendance.status} color={getStatusColor(selectedAttendance.status)} bold />
                <DetailBox label="Special Type" value={selectedAttendance.special_day_type} />
                <DetailBox label="Shift ID" value={selectedAttendance.shift_id || 'N/A'} small />
                <DetailBox label="Schedule ID" value={selectedAttendance.schedule_id || 'N/A'} small />
                <DetailBox label="Planned Time" value={selectedAttendance.planned_time_display} />
                <DetailBox label="Actual Time" value={selectedAttendance.actual_time_display} />
                <DetailBox label="Start (Plan)" value={selectedAttendance.planned_start_time} small />
                <DetailBox label="End (Plan)" value={selectedAttendance.planned_end_time} small />
                <DetailBox label="Log In" value={selectedAttendance.actual_check_in || 'N/A'} small />
                <DetailBox label="Log Out" value={selectedAttendance.actual_check_out || 'N/A'} small />
                <DetailBox label="Worked Mins" value={selectedAttendance.worked_minutes} highlight />
                <DetailBox label="Overtime Mins" value={selectedAttendance.overtime_minutes} color="#f39c12" />
                <DetailBox label="Late Mins" value={selectedAttendance.late_minutes} color="#e74c3c" />
                <DetailBox label="Early Exit Mins" value={selectedAttendance.early_exit_minutes} color="#d35400" />
                <DetailBox label="Overnight?" value={selectedAttendance.is_overnight ? 'YES' : 'NO'} />
                <DetailBox label="Roster Duty?" value={selectedAttendance.is_roster ? 'YES' : 'NO'} highlight={selectedAttendance.is_roster} />
                <DetailBox label="Type" value={selectedAttendance.is_virtual ? 'Projected (Virtual)' : 'Actual Record'} />
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={detailLabel}>Notes</label>
                  <div style={noteBox}>{selectedAttendance.notes || 'No notes available.'}</div>
                </div>
                {!selectedAttendance.is_virtual && (
                  <div style={{ gridColumn: 'span 2', fontSize: '0.75rem', color: '#bdc3c7', marginTop: '10px' }}>
                    Record ID: {selectedAttendance._id} <br />
                    Created At: {selectedAttendance.created_at || 'Unknown'}
                  </div>
                )}
              </div>
            </div>
            <div style={itemsModalFooter}>
              <button onClick={() => setSelectedAttendance(null)} style={primaryBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* --- DELETE MODAL --- */}
      {itemToDelete && (
        <div style={itemsModalOverlay}>
          <div style={{ ...itemsModalContent, maxWidth: '400px' }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <FaExclamationTriangle style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '15px' }} />
              <h3 style={{ color: '#2c3e50' }}>Delete Record?</h3>
              <p style={{ color: '#7f8c8d' }}>Are you sure you want to delete this attendance entry? This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                <button onClick={cancelDelete} style={secondaryBtn}>Cancel</button>
                <button onClick={confirmDelete} style={{ ...primaryBtn, background: '#e74c3c' }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </LocalErrorBoundary>
  );
};

// --- STYLES & SUB-COMPONENTS ---

const thStyle = {
  padding: '15px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '12px 15px', color: '#34495e', borderBottom: '1px solid #f1f2f6'
};

const SummaryWidget = ({ title, value, color, icon }) => (
  <div style={{
    background: 'white', padding: '15px', borderRadius: '12px',
    border: `1px solid ${color}30`, display: 'flex', flexDirection: 'column', gap: '5px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: '#95a5a6', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>{title}</span>
      <span style={{ color: color, fontSize: '1.2rem' }}>{icon}</span>
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2c3e50' }}>{value}</div>
  </div>
);

const ActionButton = ({ icon, color, onClick, title }) => (
  <button onClick={onClick} title={title} style={{
    background: 'white', color: color, border: `1px solid ${color}`,
    width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.9rem'
  }}
    onMouseOver={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; }}
    onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = color; }}
  >
    {icon}
  </button>
);

const DetailBox = ({ label, value, color, bold, small, highlight }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <label style={{ fontSize: '0.75rem', color: '#95a5a6', fontWeight: '600', textTransform: 'uppercase' }}>{label}</label>
    <div style={{
      fontSize: small ? '0.85rem' : '1rem',
      fontWeight: bold || highlight ? '700' : '500',
      color: color || (highlight ? '#3498db' : '#2c3e50')
    }}>
      {value || (small ? '' : 'N/A')}
    </div>
  </div>
);

// New Modal Styles (Clean & Modern)
const itemsModalOverlay = {
  position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
  background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000,
  backdropFilter: 'blur(3px)'
};
const itemsModalContent = {
  background: 'white', borderRadius: '16px', width: '90%', maxWidth: '600px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden',
  animation: 'fadeIn 0.2s ease-out'
};
const itemsModalHeader = {
  padding: '20px 25px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
};
const itemsModalBody = {
  padding: '25px', maxHeight: '70vh', overflowY: 'auto'
};
const itemsModalFooter = {
  padding: '15px 25px', background: '#f8f9fa', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end'
};
const closeBtn = {
  background: 'none', border: 'none', fontSize: '1.2rem', color: '#95a5a6', cursor: 'pointer'
};
const gridContainer = {
  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px'
};
const detailLabel = {
  display: 'block', fontSize: '0.75rem', color: '#95a5a6', fontWeight: '600', marginBottom: '5px', textTransform: 'uppercase'
};
const noteBox = {
  background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef', fontSize: '0.9rem', color: '#495057'
};
const primaryBtn = {
  padding: '10px 25px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer'
};
const secondaryBtn = {
  padding: '10px 25px', background: '#ecf0f1', color: '#7f8c8d', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer'
};

export default AttendanceView;
